import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { UserService } from '../../services/user.service';
import {
  FormBuilder,
  FormControl,
  FormArray,
  FormGroup,
  Validators,
  AbstractControl,
  NgForm,
} from '@angular/forms';
import { register as registerSwiperElements } from 'swiper/element/bundle';
import { NotificationService } from '../../services/notification.service';
import { firstValueFrom } from 'rxjs';
import { LoadingService } from '../../services/loading.service';
import { EnquireService } from '../../services/enquire.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';

registerSwiperElements();

@Component({
  selector: 'app-new-enquiry-bulk',
  templateUrl: './new-enquiry-bulk.component.html',
  styleUrls: ['./new-enquiry-bulk.component.scss'],
})
export class NewEnquiryBulkComponent implements OnInit {
  vehicleAttachments: any; 
  uploading: boolean = false;
  isLoading = false;
  locationErr: boolean = false;
  searching = false;
  selectedLocation: string;
  selectedVehicleAttachmentType: string;
  vehDetails: any;
  vehicleAttachmentList: any[] = [];
  partErrors: any[] = [];
  errorMessages: any[] = [];
  locations: any[] = [];
  showAttachmentModal: boolean = false;
  selectedAttachments: any[] = [];
  isBulkupload: boolean = true;
  isModalSubmitted = true;

  templateDownloaded = false;
  excelDataLoaded = false;
  excelPreview: any[] = [];

  excelModalForm: FormGroup;
  excelModalFormArray!: FormArray;

  showExcelModal = false;
  selectedExcelFile: File | null = null;
  excelDataFetched: boolean = false;

  makeList: any[] = [];

  // Simplified selected vehicle - only make is required
  selectedVehicle: any = {
    make: '',
    makeId: null
  };

  // Add autocomplete properties
  partDescriptionSuggestions: any[] = [];
  showSuggestions: { [key: number]: boolean } = {};
  searchTimeout: any;

  @ViewChild('excelPreviewModal') excelPreviewModal: TemplateRef<any>;

  // Vehicle document types
  vehicleDocList = [
    { id: 1, value: 'Vehicle Rc Copy' },
    { id: 2, value: 'Vehicle Front Photo' },
    { id: 3, value: 'Vehicle Rear Photo' },
    { id: 4, value: 'Vehicle Side Photo' },
    { id: 5, value: 'Parts Photo' },
  ];

  registerForm: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private user: UserService,
    private route: ActivatedRoute,
    public router: Router,
    private http: ApiService,
    private notify: NotificationService,
    private loadingService: LoadingService,
    private enquireService: EnquireService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.locationList();
    this.loadMakeList();

    this.excelModalForm = this.formBuilder.group({
      parts: this.excelModalFormArray,
    });
  }

  get partDetails() {
    return this.registerForm.get('partDetails') as FormArray;
  }

  get vehicleAttachmentDetails() {
    return this.registerForm.get('vehicleAttachmentDetails') as FormArray;
  }

  initializeForm() {
    this.registerForm = this.formBuilder.group({
      enquiryType: ['3'],
      vehNo: [''],
      chassisNo: [''],
      make: ['', Validators.required],
      model: [''],
      variant: [''],
      regDate: [''],
      fuelType: [''],
      transmissionType: [''],
      color: [''],
      noCylinders: [''],
      cc: [''],
      location: ['', Validators.required],
      partType: ['1'],
      partDetails: this.formBuilder.array([this.createPartFormGroup()]),
      vehicleAttachmentDetails: this.formBuilder.array([
        this.createVehAttachmentFormGroup(),
      ]),
      isBulkupload: [false],
    });
  }

  loadMakeList() {
    this.http.get('retailers/getMakeList').subscribe({
      next: (res: any) => {
        if (res.code === 1) {
          this.makeList = res.data || [];

          if (this.makeList.length === 1) {
            const make = this.makeList[0];
            this.registerForm.patchValue({
              make: make.id
            });
            this.selectedVehicle.make = make.title;
            this.selectedVehicle.makeId = make.id;
          }
        } else {
          this.makeList = [];
          console.error('Error loading make list:', res.message);
        }
      },
      error: (error) => {
        console.error('Error loading make list:', error);
        this.makeList = [];
        this.notify.showError('Failed to load make list', '');
      },
    });
  }

  selectBrandStep(brand: any) {
    let selectedBrand = brand;

    if (!brand.id && this.makeList.length > 0) {
      const match = this.makeList.find(b => b.name.toUpperCase() === brand.name.toUpperCase());
      if (match) {
        selectedBrand = match;
      }
    }

    // Only support Maruti (id=9)
    if (selectedBrand.id !== 9) {
      this.notify.showWarning(`We are currently not serving this brand. Support for ${selectedBrand.title || selectedBrand.name} will be available shortly.`, '');
      this.selectedVehicle.make = '';
      this.selectedVehicle.makeId = null;
      this.registerForm.get('make')?.setValue(null);
      return;
    }

    this.selectedVehicle.make = selectedBrand.title;
    this.selectedVehicle.makeId = selectedBrand.id;
    this.registerForm.get('make')?.setValue(selectedBrand.id);
  }

  isVehicleDetailsFullySelected(): boolean {
    return !!(this.selectedVehicle.makeId);
  }

  createPartFormGroup() {
    return this.formBuilder.group({
      partNo: [''],
      partDescription: [''],
      qty: [],
      partAttachmentId: [null],
      partImg: [null],
      partDefinition: [''],
      isDescriptionSelected: [false]
    });
  }

  createVehAttachmentFormGroup() {
    return this.formBuilder.group({
      attachmentType: [''],
      attachmentId: [''],
    });
  }

  onLocationChange(event: any) {
    this.selectedLocation = event.target.value;
    this.registerForm.patchValue({
      location: this.selectedLocation,
    });
  }

  locationList() {
    this.http.get('retailers/getLocationsByAuthId').subscribe((res: any) => {
      this.searching = false;
      if (res.code === 1 && res.data.records.length === 1) {
        const [firstLocation] = res.data.records;
        this.locations = [firstLocation];
        this.selectedLocation = firstLocation.branchId;
        this.registerForm.patchValue({
          location: this.selectedLocation,
        });
      } else if (res.code === 1) {
        this.locations = res.data.records;
      } else {
        this.locations = [];
      }
    });
  }

  addPart() {
    this.partDetails.push(this.createPartFormGroup());
  }

  removePart(index: number) {
    this.partDetails.removeAt(index);
  }

  onUploadTypeChange(event: any) {
    this.selectedVehicleAttachmentType = event.target.value;
  }

  newVehicleAttachment() {
    if (this.selectedVehicleAttachmentType) {
      const existingAttachment = this.vehicleAttachmentList.find(
        (attachment) =>
          attachment.attachmentType === this.selectedVehicleAttachmentType
      );
      if (existingAttachment) {
        this.notify.showError('Already Uploaded', '');
      } else {
        this.uploadImage();
      }
    }
  }

  async showVehicleAttachment(index: number, attachment: any) {
    window.open(attachment.url, '_blank');
  }

  openModalWithAttachments(attachments: any[]) {
    this.selectedAttachments = attachments;
    this.showAttachmentModal = true;
  }

  onPartDescriptionInput(event: any, index: number) {
    const keyword = event.target.value;
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (!keyword || keyword.length < 3) {
      this.showSuggestions[index] = false;
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this.fetchPartDescriptionSuggestions(keyword, index);
    }, 300);
  }

  fetchPartDescriptionSuggestions(keyword: string, index: number) {
    const makeValue = this.registerForm.get('make')?.value;
    
    if (!makeValue) {
      this.notify.showError('Please select a make first', '');
      return;
    }

    const selectedMake = this.makeList.find(make => make.id == makeValue);
    const brand = selectedMake ? selectedMake.title : makeValue;


    this.http.get('retailers/autoFetchItemDescription', {
      params: { brand: brand, keyword: keyword }
    }).subscribe({
      next: (res: any) => {
        if (res.code === 1 && res.data && res.data.length > 0) {
          this.partDescriptionSuggestions = res.data;
          this.showSuggestions[index] = true;
        } else {
          this.partDescriptionSuggestions = [];
          this.showSuggestions[index] = false;
        }
      },
      error: (error) => {
        console.error('Error fetching suggestions:', error);
        this.partDescriptionSuggestions = [];
        this.showSuggestions[index] = false;
      }
    });
  }

  selectPartDescription(suggestion: any, index: number) {
    const partDetails = this.registerForm.get('partDetails') as FormArray;
    const partDetail = partDetails.at(index);
    
    if (partDetail) {
      partDetail.patchValue({
        partDescription: suggestion.full_description || suggestion.description || suggestion.itemDescription || suggestion,
        partDefinition: suggestion.definition || '',
        isDescriptionSelected: true
      });
    }
    
    this.showSuggestions[index] = false;
    this.partDescriptionSuggestions = [];

    this.partErrors = this.partErrors.filter(
      err => !(err.index === index && err.field === 'partDescription')
    );
    
    if (index === partDetails.length - 1) {
      this.addPart();
    }
  }

  onQtyInput(index: number) {
    const partDetails = this.registerForm.get('partDetails') as FormArray;
    const qtyValue = partDetails.at(index).get('qty')?.value;
    if (qtyValue) {
      this.partErrors = this.partErrors.filter(
        err => !(err.index === index && err.field === 'qty')
      );
    }
  }

  hideSuggestions(index: number) {
    setTimeout(() => {
      this.showSuggestions[index] = false;
    }, 200);
  }

  uploadImage() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';

    fileInput.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];

        if (!file.type.startsWith('image/')) {
          this.notify.showError('Please select a valid image file.', '');
          return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
          const imgSrc = e.target?.result as string;
          const img = new Image();
          img.src = imgSrc;

          img.onload = async () => {
            const canvas = document.createElement('canvas');
            const maxWidth = 800;
            const maxHeight = 800;

            let { width, height } = img;

            if (width > height && width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            } else if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);

            try {
              const blob = await this.createBlobFromCanvas(
                canvas,
                file.type,
                0.8
              );
              const formData = new FormData();
              formData.append('filename', blob, file.name);
              formData.append('type', this.selectedVehicleAttachmentType);
              formData.append('vehNo', this.registerForm.value.vehNo || '');

              this.loadingService.show();

              const res: any = await firstValueFrom(
                this.http.filePost('retailers/enquiryImageUpload', formData)
              );

              if (res.code === 1) {
                const vehicleAttachment = {
                  attachmentType: this.selectedVehicleAttachmentType,
                  img: res.data.url,
                  attachmentId: res.data.attachmentId,
                };
                this.vehicleAttachmentList.push(vehicleAttachment);
                this.notify.showSuccess(res.message, '');
              } else {
                this.notify.showError(res.message, '');
              }
              this.loadingService.hide();
            } catch (blobError) {
              console.error('Blob creation error:', blobError);
              this.loadingService.hide();
              this.notify.showError('Error processing image.', '');
            }
          };
        };

        reader.readAsDataURL(file);
      }
    };

    fileInput.click();
  }

  createBlobFromCanvas(
    canvas: HTMLCanvasElement,
    type: string,
    quality: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas is empty or conversion to Blob failed.'));
          }
        },
        type,
        quality
      );
    });
  }

  removeVehicleAttachment(index: number, attachmentId: string) {
    this.vehicleAttachmentList.splice(index, 1);
  }

  getPartError(index: number, field: string): string {
    const error = this.partErrors.find(
      (err) => err.index === index && err.field === field
    );
    return error ? error.message : '';
  }

  showPartAttachment(index: number): boolean {
    return true;
  }

  async addPartImage(index: number) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 800;
          const maxHeight = 800;

          let { width, height } = img;

          if (width > height && width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          try {
            const blob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                (b) => (b ? resolve(b) : reject('Failed to create blob')),
                file.type,
                0.8
              );
            });

            const formData = new FormData();
            formData.append('filename', blob, 'image.png');
            formData.append('vehNo', this.registerForm.value.vehNo || '');

            this.loadingService.show();

            const res: any = await firstValueFrom(
              this.http.filePost('retailers/enquiryPartImageUpload', formData)
            );

            if (res.code === 1) {
              const partDetails = this.registerForm.get(
                'partDetails'
              ) as FormArray;
              const partDetail = partDetails.at(index);
              if (partDetail) {
                partDetail.patchValue({
                  partAttachmentId: res.data.partAttachmentId,
                  partImg: res.data.url,
                });
              }
              this.notify.showSuccess(res.message, '');
            } else {
              this.notify.showError(res.message, '');
            }
            this.loadingService.hide();
          } catch (error) {
            console.error('Image upload error:', error);
            this.loadingService.hide();
            this.notify.showError('Error processing image.', '');
          }
        };
      };
      reader.readAsDataURL(file);
    };

    input.click();
  }

  async showPartDetailsAttachment(index: number, attachment: any) {
    window.open(attachment.value.partImg, '_blank');
  }

  onSubmit() {
    this.isLoading = true;

    if (!this.selectedLocation) {
      this.notify.showError('Kindly Select the location', '');
      this.isLoading = false;
      return;
    }

    if (!this.registerForm.get('make')?.value) {
      this.notify.showError('Please select a make', '');
      this.isLoading = false;
      return;
    }

    const vehNoValue = this.registerForm.value.vehNo;
    this.registerForm.patchValue({
      location: this.selectedLocation,
      vehNo: vehNoValue ? vehNoValue.toUpperCase() : '',
    });

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.isLoading = false;
      return;
    }

    const partDetailsArray = this.registerForm.get('partDetails') as FormArray;
    this.partErrors = [];

    for (let i = partDetailsArray.length - 1; i >= 0; i--) {
      const part = partDetailsArray.at(i).value;
      const isEmpty = !part.partDescription && !part.qty && !part.partNo;
      const hasPartIdentifier = !!(part.partDescription || part.partNo);

      if (isEmpty && i !== 0) {
        partDetailsArray.removeAt(i);
      } else {
        if (!part.qty) {
          this.partErrors.push({ index: i, field: 'qty', message: 'Required' });
        }
        if (!hasPartIdentifier) {
          this.partErrors.push({
            index: i,
            field: 'partDescription',
            message: 'Enter Part No or Description',
          });
        }
      }
    }

    if (this.partErrors.length > 0) {
      this.notify.showError('Please fill all required part details.', '');
      this.isLoading = false;
      return;
    }

    const formValue = this.registerForm.value;
    formValue.isBulkupload = formValue.isBulkupload ? 1 : 1;

    if (this.vehicleAttachmentList?.length > 0) {
      formValue.vehicleAttachmentDetails = this.vehicleAttachmentList.map(
        (attachment) => ({
          attachmentId: attachment.attachmentId,
          attachmentType: attachment.attachmentType,
        })
      );
    }

    this.http.post('retailers/saveEnquiry', formValue).subscribe(
      (res: any) => {
        this.isLoading = false;
        if (res.code === 1) {
          this.notify.showSuccess(res.message, '');
          this.router.navigate(['/dashboard']);
          this.enquireService.triggerRefreshList();
        } else {
          this.notify.showError(res.message, '');
        }
      },
      (error) => {
        this.isLoading = false;
        this.notify.showError('An error occurred. Please try again.', '');
      }
    );
  }

  getAttachmentValue(attachmentTypeId: number): string {
    const attachment = this.vehicleDocList.find(
      (item) => item.id == attachmentTypeId
    );
    return attachment ? attachment.value : 'Unknown';
  }

  trackByFn(index: number): any {
    return index;
  }

  closeModal() {
    this.showAttachmentModal = false;
  }

  get isAnyPartFilled(): boolean {
    return this.partDetails.controls.some((control) => {
      const partNo = control.get('partNo')?.value?.trim();
      const desc = control.get('partDescription')?.value?.trim();
      const qty = control.get('qty')?.value;
      return (desc || partNo) && qty > 0;
    });
  }

  downloadTemplate() {
    const headers = ['Part No *', 'Description', 'Qty *'];
    const data = [headers];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);

    const headerStyle = {
      fill: { patternType: 'solid', fgColor: { rgb: '00E5E5' } },
      font: { bold: true, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    } as any;

    ['A1', 'B1', 'C1'].forEach((cell) => {
      if (ws[cell]) {
        ws[cell].s = headerStyle;
      }
    });

    ws['!cols'] = [{ wch: 18 }, { wch: 40 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([wbout], {
      type: 'application/octet-stream',
    });
    saveAs(blob, 'part_template.xlsx');

    this.templateDownloaded = true;
  }

  handleFileChange(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedExcelFile = file;
    }
  }

  onExcelUpload(): void {
    if (!this.selectedExcelFile) {
      alert('Please select a file first.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawJson = XLSX.utils.sheet_to_json(sheet, {
        defval: '',
        blankrows: false,
      });

      this.excelPreview = rawJson
        .map((row: any) => ({
          partNo: row['Part No *'] || row['Part No'] || '',
          partDescription: (row['Description'] || '').toString().trim(),
          qty:
            row['Qty *'] !== undefined
              ? row['Qty *']
              : row['Qty'] !== undefined
                ? row['Qty']
                : '',
        }))
        .filter(
          (row) => row.partDescription || row.qty.toString().trim() !== ''
        );

      const hasValid = this.excelPreview.some(
        (r) =>
          (r.partDescription.length > 0 || r.partNo.length > 0) &&
          r.qty !== '' &&
          !isNaN(Number(r.qty))
      );

      if (!hasValid) {
        alert(
          'Please make sure at least one row has a valid Part No or Description and Qty.'
        );
        return;
      }

      this.excelDataLoaded = true;
      this.openExcelModal();
    };
    reader.readAsBinaryString(this.selectedExcelFile);
  }

  openExcelModal() {
    if (this.excelPreview.length > 0) {
      this.modalService.open(this.excelPreviewModal, {
        size: 'lg',
        backdrop: 'static',
      });
    }
  }

  confirmExcelData(form: NgForm, modalRef: NgbModalRef) {
    form.control.markAllAsTouched();

    const hasError = this.excelPreview.some(
      (row) =>
        (!row.partDescription?.trim() && !row.partNo?.trim()) ||
        row.qty === null ||
        row.qty === '' ||
        isNaN(Number(row.qty))
    );

    if (hasError) {
      return;
    }

    const partDetailsArray = this.registerForm.get('partDetails') as FormArray;
    partDetailsArray.clear();

    for (const row of this.excelPreview) {
      const group = this.formBuilder.group({
        partNo: [row.partNo],
        partDescription: [row.partDescription],
        qty: [row.qty, Validators.required],
        partAttachmentId: [null],
        partImg: [null],
      });
      partDetailsArray.push(group);
    }

    this.registerForm.patchValue({ isBulkupload: true });
    this.excelDataFetched = true;

    modalRef.close();
  }

  isInvalidQty(value: any): boolean {
    return value === null || value === '' || isNaN(Number(value));
  }

  async clearSelectedExcelFile() {
    const partDetailsArray = this.registerForm.get('partDetails') as FormArray;

    if (this.excelDataFetched && partDetailsArray?.length) {
      const modalRef = this.modalService.open(ConfirmDialogComponent, {
        centered: true,
      });
      modalRef.componentInstance.title = 'Clear Uploaded Data';
      modalRef.componentInstance.message =
        'You already have part entries loaded. Do you really want to clear the file and all related part data?';
      modalRef.componentInstance.btnCancelText = 'Cancel';
      modalRef.componentInstance.btnOkText = 'Clear';

      try {
        await modalRef.result;
      } catch {
        return;
      }
    }

    this.selectedExcelFile = null;
    this.excelPreview = [];
    this.excelDataLoaded = false;
    this.excelDataFetched = false;

    if (partDetailsArray) {
      partDetailsArray.clear();
      partDetailsArray.push(this.createPartFormGroup());
    }

    this.notify.showSuccess('Uploaded file and part data cleared.', '');
  }

  removeExcelPreviewRow(index: number) {
    this.excelPreview.splice(index, 1);
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
